import React, { useState, useEffect, useRef } from 'react';
import api from '../api';

// ✅ Dummy key — Later replace with your real key!
const RECAPTCHA_SITE_KEY = '6LfNzCktAAAAAGR5ngFNIjonsM3zbvTl5N2h3_sR';

// 👆 This is Google's official TEST key — always passes
// Replace with your real key from https://www.google.com/recaptcha/admin

function Login({ onLogin }) {
  const [isRegister, setIsRegister] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', password: '', shop_name: '', phone: ''
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [message, setMessage]         = useState('');
  const [error, setError]             = useState('');
  const [forgotMsg, setForgotMsg]     = useState('');
  const [loading, setLoading]         = useState(false);
  // ✅ ADDED: Agreement checkbox state
  const [agreed, setAgreed]           = useState(false);
  const [pwdMasked, setPwdMasked] = useState(true); // true=dots, false=plain text

  // ✅ reCAPTCHA refs — one for each form
  const captchaLoginRef    = useRef(null);
  const captchaRegisterRef = useRef(null);
  const captchaForgotRef   = useRef(null);

  const [captchaLoginId,    setCaptchaLoginId]    = useState(null);
  const [captchaRegisterId, setCaptchaRegisterId] = useState(null);
  const [captchaForgotId,   setCaptchaForgotId]   = useState(null);

  useEffect(() => {
    const scriptId = 'recaptcha-script';

    const waitAndRender = () => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => renderCaptchas());
      } else {
        setTimeout(waitAndRender, 100);
      }
    };

    if (!document.getElementById(scriptId)) {
      window.__onRecaptchaLoad = () => waitAndRender();

      const script = document.createElement('script');
      script.id    = scriptId;
      script.src   = 'https://www.google.com/recaptcha/api.js?onload=__onRecaptchaLoad&render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    } else {
      waitAndRender();
    }
  }, []);

  useEffect(() => {
    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(() => {
        setTimeout(() => renderCaptchas(), 150);
      });
    }
  }, [isRegister, showForgot]);

  // ✅ NEW — Security: never let a typed password linger in memory/DOM
  // longer than it has to. Wipe it whenever the person leaves the
  // password field's context (switching tabs, unmounting the page).
  const clearPassword = () => setForm(prev => ({ ...prev, password: '' }));

  useEffect(() => {
    clearPassword();
    return () => clearPassword();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    clearPassword();
    setError('');
    setPwdMasked(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRegister]);

  const renderCaptchas = () => {
    if (!window.grecaptcha || !window.grecaptcha.render) return;

    try {
      if (captchaLoginRef.current && captchaLoginRef.current.childElementCount === 0) {
        const id = window.grecaptcha.render(captchaLoginRef.current, {
          sitekey: RECAPTCHA_SITE_KEY, theme: 'light',
        });
        setCaptchaLoginId(id);
      }
    } catch (e) { console.warn('Login captcha render skipped:', e.message); }

    try {
      if (captchaRegisterRef.current && captchaRegisterRef.current.childElementCount === 0) {
        const id = window.grecaptcha.render(captchaRegisterRef.current, {
          sitekey: RECAPTCHA_SITE_KEY, theme: 'light',
        });
        setCaptchaRegisterId(id);
      }
    } catch (e) { console.warn('Register captcha render skipped:', e.message); }

    try {
      if (captchaForgotRef.current && captchaForgotRef.current.childElementCount === 0) {
        const id = window.grecaptcha.render(captchaForgotRef.current, {
          sitekey: RECAPTCHA_SITE_KEY, theme: 'light',
        });
        setCaptchaForgotId(id);
      }
    } catch (e) { console.warn('Forgot captcha render skipped:', e.message); }
  };

  const getCaptchaToken = (id) => {
    if (!window.grecaptcha || id === null) return null;
    return window.grecaptcha.getResponse(id);
  };

  const resetCaptcha = (id) => {
    if (window.grecaptcha && id !== null) {
      window.grecaptcha.reset(id);
    }
  };

  const handleSubmit = async () => {
    setError('');

    // ✅ ADDED: Agreement validation (Register only)
    if (isRegister && !agreed) {
      setError('☐ Terms & Conditions agree பண்ணுங்கள்!');
      return;
    }

    const captchaId    = isRegister ? captchaRegisterId : captchaLoginId;
    const captchaToken = getCaptchaToken(captchaId);

    if (!captchaToken) {
      setError('reCAPTCHA complete பண்ணுங்கள்! ✅ check box click பண்ணுங்கள்.');
      return;
    }

    setLoading(true);
    try {
      if (isRegister) {
        await api.post('/auth/register', { ...form, captcha: captchaToken });
        setMessage('✅ Registration successful! Login பண்ணுங்கள்');
        setIsRegister(false);
        setAgreed(false);
        setPwdMasked(true);
        resetCaptcha(captchaRegisterId);
        clearPassword();
      } else {
        const params = new URLSearchParams();
        params.append('username', form.email);
        params.append('password', form.password);
        params.append('captcha', captchaToken);
        const res = await api.post('/auth/login', params, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        });
        const token = res.data.access_token;
        localStorage.setItem('token', token);
        clearPassword(); // ✅ NEW: clear before navigating away
        onLogin(token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error occurred!');
      resetCaptcha(captchaId);
      setPwdMasked(true);
      clearPassword();
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    setForgotMsg('');
    const captchaToken = getCaptchaToken(captchaForgotId);

    if (!captchaToken) {
      setForgotMsg('❌ reCAPTCHA complete பண்ணுங்கள்!');
      return;
    }

    try {
      await api.post(`/auth/forgot-password?email=${forgotEmail}&captcha=${captchaToken}`);
      setForgotMsg('✅ Reset link உங்கள் email-ல் அனுப்பப்பட்டது!');
      resetCaptcha(captchaForgotId);
    } catch (err) {
      setForgotMsg('❌ Error! Try again.');
      resetCaptcha(captchaForgotId);
    }
  };

  // ─── Forgot Password Page ───
  if (showForgot) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.logo}>📦 JSMSTOCK</h1>
          <h2 style={styles.title}>🔑 Password Reset</h2>
          {forgotMsg && (
            <p style={forgotMsg.startsWith('✅') ? styles.success : styles.error}>
              {forgotMsg}
            </p>
          )}
          <input
            style={styles.input}
            placeholder="உங்கள் Email"
            type="email"
            autoComplete="off"
            value={forgotEmail}
            onChange={e => setForgotEmail(e.target.value)}
          />
          <div style={styles.captchaWrap}>
            <div ref={captchaForgotRef}></div>
          </div>
          <button style={styles.button} onClick={handleForgot}>
            Reset Link அனுப்பு
          </button>
          <p style={styles.toggle} onClick={() => setShowForgot(false)}>
            ← Back to Login
          </p>
        </div>
      </div>
    );
  }

  // ─── Login / Register Page ───
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.logo}>📦 JSMSTOCK</h1>
        <p style={styles.tagline}>உங்கள் கடை, உங்கள் கையில்!</p>
        <h2 style={styles.title}>
          {isRegister ? '📝 புது Account' : '🔐 Login'}
        </h2>

        {message && <p style={styles.success}>{message}</p>}
        {error   && <p style={styles.error}>{error}</p>}

        {isRegister && (
          <>
            <input style={styles.input} placeholder="உங்கள் பெயர்"
              autoComplete="off"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} />
            <input style={styles.input} placeholder="கடை பெயர்"
              autoComplete="off"
              value={form.shop_name}
              onChange={e => setForm({ ...form, shop_name: e.target.value })} />
            <input
              placeholder="📱 Mobile Number *" type="tel" maxLength={10}
              autoComplete="off"
              style={{ ...styles.input, border: '2px solid #E74C3C' }}
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })} />
          </>
        )}

        <input style={styles.input} placeholder="Email" type="email"
          autoComplete="off"
          value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })} />
        {/* Password field with show/hide toggle */}
        <div style={{ position: 'relative', width: '100%', margin: '8px 0' }}>
          <input
            style={{ ...styles.input, paddingRight: '44px', margin: 0 }}
            placeholder="Password"
            type={pwdMasked ? 'password' : 'text'}
            autoComplete={isRegister ? 'new-password' : 'current-password'}
            value={form.password}
            onChange={e => setForm({ ...form, password: e.target.value })}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          />
          <span
            style={{
              position: 'absolute', right: '12px', top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer', fontSize: '20px', userSelect: 'none',
            }}
            onClick={() => setPwdMasked(!pwdMasked)}
            title={pwdMasked ? 'Password காட்டு' : 'Password மறை'}
          >
            {pwdMasked ? '👁️' : '🙈'}
          </span>
        </div>

        {/* ✅ reCAPTCHA — Login */}
        {!isRegister && (
          <div style={styles.captchaWrap}>
            <div ref={captchaLoginRef}></div>
          </div>
        )}

        {/* ✅ reCAPTCHA — Register */}
        {isRegister && (
          <div style={styles.captchaWrap}>
            <div ref={captchaRegisterRef}></div>
          </div>
        )}

        {/* ✅ ADDED: Agreement Checkbox — Register only */}
        {isRegister && (
          <>
            <div style={styles.agreeWrap}>
              <input
                type="checkbox"
                id="agree_terms"
                checked={agreed}
                onChange={e => {
                  setAgreed(e.target.checked);
                  if (error.includes('agree')) setError('');
                }}
                style={styles.checkbox}
              />
              <label htmlFor="agree_terms" style={styles.agreeLabel}>
                நான் JSMSTOCK-இன்{' '}
                <a href="/terms" target="_blank" style={styles.agreeLink}>
                  Terms & Conditions
                </a>
                ,{' '}
                <a href="/privacy" target="_blank" style={styles.agreeLink}>
                  Privacy Policy
                </a>
                {' '}மற்றும்{' '}
                <a href="/refund" target="_blank" style={styles.agreeLink}>
                  Refund Policy
                </a>
                {' '}படித்து agree பண்றேன்! *
              </label>
            </div>

            {/* Agreement status hint */}
            {!agreed && (
              <p style={styles.agreeHint}>
                ☐ Register பண்ண இதை tick பண்ணவும்!
              </p>
            )}
            {agreed && (
              <p style={styles.agreeOk}>✅ Terms agreed!</p>
            )}
          </>
        )}

        <button
          style={{
            ...styles.button,
            // ✅ ADDED: Disable button if register and not agreed
            opacity: loading || (isRegister && !agreed) ? 0.6 : 1,
            cursor: (isRegister && !agreed) ? 'not-allowed' : 'pointer',
          }}
          onClick={handleSubmit}
          disabled={loading || (isRegister && !agreed)}
        >
          {loading ? '⏳ Please wait...' : isRegister ? '✅ Register' : '🔐 Login'}
        </button>

        <p style={styles.toggle} onClick={() => {
          setIsRegister(!isRegister);
          setError(''); setMessage('');
          setAgreed(false); // ✅ ADDED: Reset agreement when switching
        }}>
          {isRegister
            ? 'Already account? Login பண்ணுங்கள்'
            : 'புது account? Register பண்ணுங்கள்'}
        </p>

        {!isRegister && (
          <p style={styles.forgot} onClick={() => setShowForgot(true)}>
            Password மறந்துவிட்டதா?
          </p>
        )}

        {/* ✅ ADDED: Footer links */}
        <div style={styles.footer}>
          <a href="/terms"   target="_blank" style={styles.footLink}>Terms</a>
          <span style={styles.footDot}>•</span>
          <a href="/privacy" target="_blank" style={styles.footLink}>Privacy</a>
          <span style={styles.footDot}>•</span>
          <a href="/refund"  target="_blank" style={styles.footLink}>Refund</a>
        </div>

        {/* ✅ NEW: Security note */}
        <div style={styles.securityNote}>
          🔒 Passwords are never stored in this browser tab
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh', backgroundColor: '#f0f2f5',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  card: {
    backgroundColor: 'white', padding: '40px', borderRadius: '15px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    width: '100%', maxWidth: '420px', textAlign: 'center',
  },
  logo:    { color: '#2c3e50', marginBottom: '5px' },
  tagline: { color: '#666', marginBottom: '20px', fontSize: '14px' },
  title:   { color: '#2c3e50', marginBottom: '20px' },
  input: {
    display: 'block', width: '100%', padding: '12px', margin: '8px 0',
    borderRadius: '8px', border: '1px solid #ddd',
    fontSize: '16px', boxSizing: 'border-box',
  },
  captchaWrap: {
    display: 'flex', justifyContent: 'center', margin: '12px 0',
  },
  button: {
    backgroundColor: '#2c3e50', color: 'white', padding: '12px',
    border: 'none', borderRadius: '8px', fontSize: '16px',
    width: '100%', marginTop: '10px',
    transition: 'opacity 0.2s',
  },
  toggle: { color: '#3498db', cursor: 'pointer', marginTop: '15px', fontSize: '14px' },
  forgot: { color: '#e74c3c', cursor: 'pointer', marginTop: '8px', fontSize: '13px' },
  success: { color: 'green', fontWeight: 'bold' },
  error:   { color: 'red', fontSize: '13px' },

  // ✅ ADDED: Agreement styles
  agreeWrap: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    margin: '14px 0 4px',
    textAlign: 'left',
    background: '#FFF9E6',
    border: '1.5px solid #C9A84C',
    borderRadius: '8px',
    padding: '10px 12px',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    marginTop: '2px',
    cursor: 'pointer',
    accentColor: '#2C3E50',
    flexShrink: 0,
  },
  agreeLabel: {
    fontSize: '12px',
    color: '#444',
    cursor: 'pointer',
    lineHeight: '1.6',
    textAlign: 'left',
  },
  agreeLink: {
    color: '#3498DB',
    fontWeight: '700',
    textDecoration: 'none',
  },
  agreeHint: {
    color: '#E67E22',
    fontSize: '11px',
    margin: '2px 0 6px',
    textAlign: 'left',
  },
  agreeOk: {
    color: '#27AE60',
    fontSize: '11px',
    margin: '2px 0 6px',
    textAlign: 'left',
    fontWeight: '600',
  },

  // ✅ ADDED: Footer link styles
  footer: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '6px',
  },
  footLink: {
    color: '#999', fontSize: '11px', textDecoration: 'none',
  },
  footDot: { color: '#CCC', fontSize: '11px' },

  // ✅ NEW
  securityNote: {
    textAlign: 'center', color: '#aaa',
    fontSize: '10px', marginTop: '10px',
  },
};

export default Login;

